import './App.css';
import {useEffect, useState} from "react";
import {useHistory} from "react-router-dom";

const SignUp = () => {
    const [name, setName] = useState("");
    const [emblem, setEmblem] = useState("");
    const [fact, setFact] = useState("");
    const [subjectFact, setSubjectFact] = useState("");
    const [subject, setSubject] = useState("the group");
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const [result, setResult] = useState(null); // {name, token, status, character?}
    const history = useHistory();

    // Pull the per-event subject for the "fact about ___" prompt.
    useEffect(() => {
        fetch('/event-config', {credentials: 'include'})
            .then(r => r.ok ? r.json() : null)
            .then(c => { if (c && c.subject) setSubject(c.subject); })
            .catch(() => {});
    }, []);

    // On mount, check if already registered (cookie + user record).
    useEffect(() => {
        fetch('/users/current', {credentials: 'include'})
            .then(r => r.ok ? r.json() : null)
            .then(u => {
                if (u && u.token) setResult({name: u.name, token: u.token, character: u.character});
            })
            .catch(() => {});
    }, []);

    // Poll character status while processing.
    useEffect(() => {
        if (!result || !result.token) return;
        if (result.character && result.character.status === 'ready') return;
        if (result.character && result.character.status === 'error') return;
        const interval = setInterval(() => {
            fetch('/users/current', {credentials: 'include'})
                .then(r => r.ok ? r.json() : null)
                .then(u => {
                    if (u) setResult(prev => ({...prev, name: u.name, token: u.token, character: u.character}));
                })
                .catch(() => {});
        }, 2000);
        return () => clearInterval(interval);
    }, [result]);

    const validate = () => {
        const errors = {};
        if (!name.trim()) errors.name = "Required";
        if (!emblem.trim()) errors.emblem = "Required";
        if (!fact.trim()) errors.fact = "Required";
        if (!subjectFact.trim()) errors.subjectFact = "Required";
        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const submit = () => {
        setSubmitted(true);
        if (!validate()) return;
        setSubmitting(true);
        fetch('/users/register', {
            method: 'POST',
            credentials: 'include',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({name: name.trim(), emblem: emblem.trim(), fact: fact.trim(), subjectFact: subjectFact.trim()})
        }).then(async r => {
            const data = await r.json();
            if (!r.ok) {
                if (data.error === 'missing_field') {
                    const errs = {};
                    (data.fields || []).forEach(f => errs[f] = "Required");
                    setFieldErrors(errs);
                }
                setSubmitting(false);
                return;
            }
            setResult(data);
            setSubmitting(false);
        }).catch(() => {
            setSubmitting(false);
            setFieldErrors({_form: "Something went wrong. Try again."});
        });
    };

    if (result) return <StatusView result={result} setResult={setResult}/>;

    return (
        <div style={outerStyle(false)}>
            <Logo/>
            <div style={formWrapperStyle}>
                <div style={{fontSize: 38, fontWeight: 'bold', marginTop: 20, marginBottom: 16, textAlign: 'center'}}>Registration</div>
                <div style={{fontSize: 20, opacity: 0.7, marginBottom: 20, lineHeight: 1.5, textAlign: 'center'}}>
                    Already registered? <a href="/login" style={{color: '#36ff36', textDecoration: 'underline'}}>Login here.</a> If you've lost your code, ask Cole.
                </div>

                <Field
                    label="Name"
                    placeholder="e.g. 'Cole'"
                    value={name} setValue={setName}
                    error={submitted && fieldErrors.name}/>
                <Field
                    label="Theme"
                    help="Used to style your profile image."
                    placeholder="e.g. 'sharks', 'neon', 'drum sticks'"
                    value={emblem} setValue={setEmblem}
                    error={submitted && fieldErrors.emblem}/>
                <Field
                    label="Interesting Fact About You"
                    help="A hobby, an unusual skill, a weird life experience. Anything memorable."
                    placeholder="I once accidentally got on stage at a Yeah Yeah Yeahs concert."
                    value={fact} setValue={setFact} multiline
                    error={submitted && fieldErrors.fact}/>
                <Field
                    label={`Fact or Story About ${subject}`}
                    help={`A memory, a quirk, something surprising or funny about ${subject}. The wilder and more specific, the better.`}
                    placeholder={`${subject} once hosted a hot dog eating contest.`}
                    value={subjectFact} setValue={setSubjectFact} multiline
                    error={submitted && fieldErrors.subjectFact}/>

                {fieldErrors._form && <div style={{color: '#ff5a5a', marginBottom: 16}}>{fieldErrors._form}</div>}

                <button
                    disabled={submitting}
                    onClick={submit}
                    style={{
                        marginTop: 32,
                        width: '100%',
                        padding: '20px',
                        fontSize: 28,
                        fontWeight: 600,
                        color: 'white',
                        backgroundColor: 'black',
                        border: '2px solid white',
                        borderRadius: 4,
                        cursor: submitting ? 'wait' : 'pointer',
                        opacity: submitting ? 0.6 : 1,
                    }}
                >
                    {submitting ? 'Registering…' : 'Register'}
                </button>
            </div>
        </div>
    );
};

const StatusView = ({result, setResult}) => {
    const status = result.character && result.character.status;
    const isReady = status === 'ready';
    const isError = status === 'error';
    const isProcessing = !isReady && !isError;

    // Fake-but-honest progress: fills over ~20s, holds at the last notch
    // until the real status lands.
    const [progress, setProgress] = useState(0);
    useEffect(() => {
        if (!isProcessing) { setProgress(0); return; }
        const id = setInterval(() => setProgress(p => Math.min(6, p + 1)), 3300);
        return () => clearInterval(id);
    }, [isProcessing]);

    const retry = () => {
        // Optimistically flip back to the spinner; polling resumes and picks
        // up the real status from the server.
        setResult(prev => ({...prev, character: {...prev.character, status: 'processing'}}));
        fetch('/users/retry-character', {method: 'POST', credentials: 'include'}).catch(() => {});
    };

    const codeWordBox = (
        <div style={{
            padding: 20,
            border: '2px solid #36ff36',
            borderRadius: 8,
            textAlign: 'center',
            marginBottom: 24,
        }}>
            <div style={{fontSize: 14, opacity: 0.7, marginBottom: 6}}>YOUR CODE WORD</div>
            <div style={{fontSize: 42, fontWeight: 'bold', color: '#36ff36', letterSpacing: 2}}>{result.token}</div>
            <div style={{fontSize: 14, opacity: 0.8, marginTop: 10}}>Screenshot this. You'll need it if you get logged out.</div>
        </div>
    );

    return (
        <div style={outerStyle(isReady)}>
            {!isReady && !isError && <Logo/>}
            <div style={formWrapperStyle}>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

                {!isReady && !isError && (
                    <div style={{textAlign: 'center', marginTop: 60, marginBottom: 40}}>
                        <div style={{
                            width: 48,
                            height: 48,
                            border: '4px solid rgba(255,255,255,0.2)',
                            borderTop: '4px solid white',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            margin: '0 auto 24px',
                        }}/>
                        <div style={{fontSize: 20, marginBottom: 8}}>Creating your character</div>
                        <div style={{fontSize: 26, letterSpacing: 4, marginBottom: 8}}>
                            {'🟢'.repeat(progress) + '⚫'.repeat(6 - progress)}
                        </div>
                        <div style={{fontSize: 16, opacity: 0.6}}>This takes about 30 seconds.</div>
                    </div>
                )}

                {isReady && (
                    <>
                        <div style={{
                            fontSize: 40,
                            fontWeight: 'bold',
                            textAlign: 'center',
                        }}>
                            Ready to Play ✅
                        </div>
                        <hr style={{border: 0, borderTop: '1px solid white', margin: '4px 0px 12px 0px'}}/>
                        <div style={{fontSize: 36, textAlign: 'center', marginBottom: 8}}>
                            {result.name}
                        </div>
                        {result.character && result.character.nickname && (
                            <div style={{fontSize: 24, textAlign: 'center', marginBottom: 20, fontStyle: 'italic', opacity: 0.85}}>
                                {result.character.nickname}
                            </div>
                        )}
                        {result.character && result.character.image_url && (
                            <img
                                src={result.character.image_url}
                                alt="character"
                                style={{width: '140%', marginLeft: '-20%', display: 'block', borderRadius: 8, marginBottom: 36}}
                            />
                        )}
                    </>
                )}

                {isError && (
                    <>
                        <div style={{fontSize: 48, fontWeight: 'bold', marginTop: 40, marginBottom: 24, textAlign: 'center'}}>
                            Something went wrong
                        </div>
                        <div style={{textAlign: 'center', fontSize: 18, color: '#ff5a5a', marginBottom: 24}}>
                            Character generation failed. Your answers are saved.
                        </div>
                        <button
                            onClick={retry}
                            style={{
                                width: '100%',
                                padding: '20px',
                                fontSize: 28,
                                fontWeight: 600,
                                color: 'white',
                                backgroundColor: 'black',
                                border: '2px solid #36ff36',
                                borderRadius: 4,
                                cursor: 'pointer',
                                marginBottom: 24,
                            }}
                        >
                            Try Again
                        </button>
                    </>
                )}

                {codeWordBox}
            </div>
        </div>
    );
};

const Logo = () => (
    <video
        src="logo-video.mp4"
        autoPlay loop muted playsInline
        style={{width: '100%', display: 'block'}}
    />
);

const outerStyle = (ready = false) => ({
    minHeight: '100vh',
    paddingTop: 20,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    overflowX: 'hidden',
    background: ready
        ? 'radial-gradient(ellipse at top, rgba(54,255,54,0.18) 0%, #000 60%)'
        : '#000',
    transition: 'background 600ms ease',
});

const formWrapperStyle = {
    width: '90%',
    padding: '0 12px 20px',
    boxSizing: 'border-box',
};

const Field = ({label, help, placeholder, value, setValue, multiline, error}) => {
    const commonStyle = {
        width: '100%',
        padding: '16px 18px',
        fontSize: 22,
        color: 'white',
        backgroundColor: 'black',
        border: error ? '1px solid #ff5a5a' : '1px solid rgba(255,255,255,0.4)',
        boxSizing: 'border-box',
        fontFamily: 'inherit',
    };
    return (
        <div style={{textAlign: 'left', marginBottom: 28}}>
            <label style={{display: 'block', fontSize: 22, fontWeight: 600, marginBottom: 4}}>{label}</label>
            {help && <div style={{fontSize: 16, opacity: 0.6, marginBottom: 10, lineHeight: 1.4}}>{help}</div>}
            <div style={{position: 'relative'}}>
                {multiline
                    ? <textarea className="inputbox" rows={3} placeholder={placeholder} value={value} onChange={e => setValue(e.target.value)} style={{...commonStyle, resize: 'vertical'}}/>
                    : <input className="inputbox" type="text" placeholder={placeholder} value={value} onChange={e => setValue(e.target.value)} style={commonStyle}/>
                }
                <span className="focus-border"><i/></span>
            </div>
            {error && <div style={{color: '#ff5a5a', fontSize: 14, marginTop: 6}}>{error}</div>}
        </div>
    );
};

export default SignUp;
